import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, ButtonBase, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Tooltip, Typography } from '@mui/material';
import { ApartmentOutlined as CustomerIcon, DeleteOutline as DeleteIcon, EditOutlined as EditIcon } from '@mui/icons-material';
import SidePanel, { PanelSection } from '../SidePanel';
import { createCustomer, deleteCustomer, updateCustomer, validateCustomerForm } from '../../utils/customersService';
import { parseLocalDate } from '../../utils/dateHelpers';
import { GREEN, GREEN_DARK, GREEN_SOFT, INK, MUTED } from '../home/homeTheme';

const EMPTY_FORM = { name: '', description: '' };
const fmtDate = (v) => (v ? new Date(v).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
const fmtDay = (v) => (v ? parseLocalDate(String(v).slice(0, 10)).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');

/** Icona del cliente: quadrato verde chiaro con l'edificio. */
export function CustomerBadge({ size = 32 }) {
  return (
    <Box sx={{ width: size, height: size, borderRadius: size > 40 ? 3 : 2, bgcolor: GREEN_SOFT, color: GREEN_DARK, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <CustomerIcon sx={{ fontSize: size * 0.56 }} />
    </Box>
  );
}

function StatusChip({ active }) {
  return (
    <Box component="span" sx={{ px: 0.75, py: 0.1, borderRadius: 999, fontSize: 11.5, fontWeight: 700, whiteSpace: 'nowrap', bgcolor: active ? GREEN_SOFT : 'var(--rt-track)', color: active ? GREEN_DARK : MUTED }}>
      {active ? 'Active' : 'Inactive'}
    </Box>
  );
}

/**
 * Pannello laterale di un cliente: descrizione, progetti, contratti, dati di sistema;
 * modifica, creazione ed eliminazione (bloccata se il cliente ha progetti o contratti).
 */
export default function CustomerPanel({ open, customer, creating, customers, projects, contracts, onClose, onSaved, onDeleted, onOpenProject }) {
  const customerId = customer?.id;
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    setEditing(!!creating);
    setForm(!creating && customer ? { name: customer.name || '', description: customer.description || '' } : EMPTY_FORM);
    setErrors({});
    setConfirmDelete(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, creating]);

  const showForm = creating || editing;
  const sortedProjects = [...projects].sort((a, b) => Number(!!b.active) - Number(!!a.active) || String(a.name).localeCompare(String(b.name)));
  const activeProjects = projects.filter((p) => p.active).length;
  const linked = projects.length + (contracts?.length || 0);

  const set = (field) => (value) => {
    setForm((f) => ({ ...f, [field]: value }));
    setErrors({});
  };
  const save = async () => {
    const e = validateCustomerForm(form, customers, creating ? null : customerId);
    setErrors(e);
    if (Object.keys(e).length) return;
    setSaving(true);
    const payload = { name: form.name.trim(), description: form.description.trim() || null };
    try {
      const res = creating ? await createCustomer(payload) : await updateCustomer(customerId, payload);
      setEditing(false);
      onSaved(creating ? res.customer?.id ?? res.id : customerId, creating ? 'Customer created' : 'Customer updated');
    } catch (err) {
      setErrors({ general: `Not saved: ${err.message}` });
    } finally {
      setSaving(false);
    }
  };
  const cancel = () => {
    if (creating) {
      onClose();
      return;
    }
    setEditing(false);
    setForm({ name: customer.name || '', description: customer.description || '' });
    setErrors({});
  };
  const remove = async () => {
    setDeleting(true);
    try {
      await deleteCustomer(customerId);
      setConfirmDelete(false);
      onDeleted('Customer deleted');
    } catch (err) {
      setConfirmDelete(false);
      setErrors({ general: `Not deleted: ${err.message}` });
    } finally {
      setDeleting(false);
    }
  };

  if (!customer && !creating) return <SidePanel open={false} onClose={onClose} />;

  return (
    <>
      <SidePanel
        open={open}
        onClose={onClose}
        busy={saving || deleting}
        icon={<CustomerBadge size={46} />}
        title={creating ? 'New customer' : customer.name}
        subtitle={creating ? null : `${activeProjects} active project${activeProjects === 1 ? '' : 's'} · ${projects.length} in total`}
        actions={
          !showForm && (
            <Button variant="outlined" color="inherit" size="small" startIcon={<EditIcon />} onClick={() => setEditing(true)} sx={{ textTransform: 'none', borderColor: 'var(--rt-pill-border)', fontWeight: 600 }}>
              Edit
            </Button>
          )
        }
        footer={
          showForm && (
            <>
              <Button color="inherit" onClick={cancel} disabled={saving} sx={{ textTransform: 'none' }}>
                Cancel
              </Button>
              <Button variant="contained" color="success" onClick={save} disabled={saving} sx={{ textTransform: 'none', fontWeight: 700, boxShadow: 'none', minWidth: 120, bgcolor: GREEN }}>
                {saving ? <CircularProgress size={18} color="inherit" /> : creating ? 'Create customer' : 'Save changes'}
              </Button>
            </>
          )
        }
      >
        {showForm ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1.5 }}>
            <TextField label="Name" size="small" value={form.name} onChange={(e) => set('name')(e.target.value)} error={!!errors.name} helperText={errors.name} autoFocus={creating} />
            <TextField label="Description" size="small" multiline minRows={3} value={form.description} onChange={(e) => set('description')(e.target.value)} />
            {errors.general && <Alert severity="error">{errors.general}</Alert>}
          </Box>
        ) : (
          <>
            {errors.general && (
              <Alert severity="error" sx={{ mb: 1.5 }}>
                {errors.general}
              </Alert>
            )}
            <Typography sx={{ fontSize: 13.5, color: customer.description ? INK : MUTED, pb: 1.75, whiteSpace: 'pre-line' }}>
              {customer.description || 'No description.'}
            </Typography>

            <PanelSection title={`Projects · ${projects.length}`}>
              {!projects.length ? (
                <Typography sx={{ fontSize: 13.5, color: MUTED }}>No projects for this customer.</Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                  {sortedProjects.map((p) => (
                    <ButtonBase
                      key={p.id}
                      onClick={() => onOpenProject(p.id)}
                      sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-start', textAlign: 'left', borderRadius: 2, px: 0.75, py: 0.6, mx: -0.75, '&:hover': { bgcolor: 'var(--rt-hover)' } }}
                    >
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography noWrap sx={{ fontSize: 13.5, fontWeight: 600, color: p.active ? INK : MUTED }}>
                          {p.name}
                        </Typography>
                        <Typography noWrap sx={{ fontSize: 12, color: MUTED }}>
                          {[p.project_code, p.start_date && `from ${fmtDay(p.start_date)}`, p.end_date && `to ${fmtDay(p.end_date)}`].filter(Boolean).join(' · ') || '—'}
                        </Typography>
                      </Box>
                      <StatusChip active={!!p.active} />
                    </ButtonBase>
                  ))}
                </Box>
              )}
            </PanelSection>

            <PanelSection title={`Contracts${contracts ? ` · ${contracts.length}` : ''}`}>
              {!contracts ? (
                <Typography sx={{ fontSize: 13.5, color: MUTED }}>Contracts are not available right now.</Typography>
              ) : !contracts.length ? (
                <Typography sx={{ fontSize: 13.5, color: MUTED }}>No contracts for this customer.</Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {contracts.map((c) => (
                    <Box key={c.id}>
                      <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: INK }}>
                        {c.yubiq_code ? (
                          <Box component="span" sx={{ fontFamily: 'monospace', fontSize: 12.5, bgcolor: 'var(--rt-track)', px: 0.75, py: 0.1, borderRadius: 1, mr: 0.75 }}>
                            {c.yubiq_code}
                          </Box>
                        ) : null}
                        {c.tipologia_servizio || c.description || 'Contract'}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: MUTED }}>
                        {[c.cluster, c.scadenza_contratto && `expires ${fmtDay(c.scadenza_contratto)}`, c.stato_rinnovo].filter(Boolean).join(' · ') || '—'}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </PanelSection>

            <PanelSection title="System">
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', rowGap: 1, columnGap: 2 }}>
                {[
                  ['Customer ID', customer.id],
                  ['Created', fmtDate(customer.created_at)],
                  ['Last updated', fmtDate(customer.updated_at)],
                ].map(([label, value]) => (
                  <Box key={label}>
                    <Typography sx={{ fontSize: 12, color: MUTED }}>{label}</Typography>
                    <Typography sx={{ fontSize: 13.5, color: INK }}>{value}</Typography>
                  </Box>
                ))}
              </Box>
            </PanelSection>

            {/* Eliminazione: solo per clienti senza progetti né contratti */}
            <Box sx={{ pt: 1.5 }}>
              <Tooltip title={linked ? 'Move or delete its projects and contracts first' : ''}>
                <span>
                  <Button
                    color="error"
                    size="small"
                    startIcon={<DeleteIcon />}
                    disabled={!!linked}
                    onClick={() => setConfirmDelete(true)}
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                  >
                    Delete customer
                  </Button>
                </span>
              </Tooltip>
              {!!linked && (
                <Typography sx={{ fontSize: 12, color: MUTED }}>
                  It can’t be deleted while it has projects or contracts.
                </Typography>
              )}
            </Box>
          </>
        )}
      </SidePanel>

      <Dialog open={confirmDelete} onClose={() => !deleting && setConfirmDelete(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Delete {customer?.name}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">The customer will be removed permanently.</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button color="inherit" onClick={() => setConfirmDelete(false)} disabled={deleting} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={remove} disabled={deleting} sx={{ textTransform: 'none', fontWeight: 700, boxShadow: 'none' }}>
            {deleting ? <CircularProgress size={18} color="inherit" /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
